import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Grid, Card, Button,
    alpha, Stack, Chip, Avatar, Divider, Paper, Tab, Tabs,
    Fade, Grow, useMediaQuery, useTheme,
} from '@mui/material';
import {
    School as SchoolIcon,
    LocationOn as LocationIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Language as WebIcon,
    CheckCircle as CheckIcon,
    Person as PersonIcon,
    MenuBook as BookIcon,
    EmojiEvents as TrophyIcon,
    Groups as GroupsIcon,
    CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import Navbar from '../Navbar';
import HomeFooter from '../HomeFooter';

// ============ IMPORT ALL COLLEGE COMPONENTS ============
import JurongCollegePage from './JurongCollegePage';
import TrentCollegePage from './TrentCollegePage';
import NewtonCollegePage from './NewtonCollegePage';
import AEGCollegePage from './AEGCollegePage';
import AlliansCollegePage from './AlliansCollegePage';

const BRAND_GRADIENT = 'linear-gradient(135deg, #1d748b 0%, #52d370 100%)';

// ==================== REUSABLE COLLEGE LOGO COMPONENT ====================
const CollegeLogo = ({ college, size = 70, selected = false, forTab = false }) => {
    if (college.logo) {
        return (
            <Box
                sx={{
                    width: size,
                    height: size,
                    flexShrink: 0,
                    borderRadius: '50%',
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    transition: 'all 0.3s',
                    boxShadow: selected
                        ? `0 4px 14px ${alpha(college.color, 0.35)}`
                        : '0 2px 8px rgba(0,0,0,0.08)',
                    border: forTab
                        ? `2px solid ${selected ? college.color : alpha(college.color, 0.2)}`
                        : 'none',
                }}
            >
                <Box
                    component="img"
                    src={college.logo}
                    alt={college.name}
                    sx={{
                        width: '88%',
                        height: '88%',
                        objectFit: 'contain',
                    }}
                />
            </Box>
        );
    }

    return (
        <Avatar
            sx={{
                width: size,
                height: size,
                background: forTab
                    ? (selected ? college.color : alpha(college.color, 0.15))
                    : college.color,
                color: forTab && !selected ? college.color : 'white',
                fontWeight: 800,
                fontSize: size > 50 ? '1.5rem' : '0.85rem',
                transition: 'all 0.3s',
            }}
        >
            {college.shortName}
        </Avatar>
    );
};

// ==================== 5 COLLEGES DATA ====================
const collegesData = [
    {
        id: 0,
        name: 'Jurong Academy',
        shortName: 'JA',
        tagline: 'Excellence in Education Since 1995',
        color: '#1d748b',
        established: '1995',
        location: 'Jurong East, Singapore',
        phone: '+65 6123 4567',
        email: 'info@jurongacademy.edu',
        website: 'https://ja.edu.sg',
        about: 'Jurong Academy is a premier institution committed to academic excellence and holistic development.',
        courses: ['Computer Science', 'Business Administration', 'Engineering', 'Arts & Design', 'Health Sciences'],
        facilities: ['Modern Library', 'Science Labs', 'Sports Complex', 'Digital Classrooms', 'Hostel'],
        achievements: ['Top Ranked College 2023', '98% Placement Rate', 'National Sports Champions'],
        students: '2,500+',
        faculty: '150+',
        logo: '/images/jurong.png',
        component: JurongCollegePage,
    },
    {
        id: 1,
        name: 'Trent International College',
        shortName: 'TIC',
        tagline: 'Global Learning, Local Values',
        color: '#dc3545',
        established: '2001',
        location: 'Trent Road, Singapore',
        phone: '+65 6234 5678',
        email: 'admissions@trentcollege.edu',
        website: 'https://trentinternationalinstitute.impulzlms.com',
        about: 'Trent International College offers world-class education with an international curriculum.',
        courses: ['International Business', 'Information Technology', 'Hospitality Management', 'Psychology', 'Media Studies'],
        facilities: ['International Library', 'Tech Hub', 'Cafeteria', 'Auditorium', 'Sports Ground'],
        achievements: ['Best International College 2023', 'Global Exchange Programs', 'Research Excellence Award'],
        students: '1,800+',
        faculty: '120+',
        logo: '/images/trend.png',
        component: TrentCollegePage,
    },
    {
        id: 2,
        name: 'Newton International College',
        shortName: 'NIC',
        tagline: 'Innovation Through Education',
        color: '#6f42c1',
        established: '2005',
        location: 'Newton Circle, Singapore',
        phone: '+65 6345 6789',
        email: 'contact@newtoncollege.edu',
        website: 'https://newtoninternationalcollege.impulzlms.com',
        about: 'Newton International College focuses on STEM education and research.',
        courses: ['Engineering', 'Data Science', 'Biotechnology', 'Mathematics', 'Physics'],
        facilities: ['Research Labs', 'Innovation Center', 'Robotics Lab', 'Library', 'Sports Arena'],
        achievements: ['Innovation Award 2023', 'STEM Excellence', 'Top Research Output'],
        students: '1,500+',
        faculty: '100+',
        logo: '/images/newton.png',
        component: NewtonCollegePage,
    },
    {
        id: 3,
        name: 'AEG International College',
        shortName: 'AEG',
        tagline: 'Empowering Future Leaders',
        color: '#fd7e14',
        established: '2008',
        location: 'AEG Campus, Singapore',
        phone: '+65 6456 7890',
        email: 'info@aegcollege.edu',
        website: 'https://aeginternationalcollege.impulzlms.com',
        about: 'AEG International College is an educational institution in Singapore offering professional and higher-diploma programmes.',
        courses: ['Business Management', 'Entrepreneurship', 'Marketing', 'Finance', 'Human Resources'],
        facilities: ['Business Incubator', 'Conference Halls', 'Library', 'Cafeteria', 'Gym'],
        achievements: ['Best Business College', 'Entrepreneur Hub', 'Industry Partnerships'],
        students: '1,200+',
        faculty: '80+',
        logo: '/images/aeg.png',
        component: AEGCollegePage,
    },
    {
        id: 4,
        name: 'Allians International College',
        shortName: 'AIC',
        tagline: 'United in Excellence',
        color: '#198754',
        established: '2010',
        location: 'Allians Park, Singapore',
        phone: '+65 6567 8901',
        email: 'hello@allianscollege.edu',
        website: 'https://alliansinternationalcollege.impulzlms.com',
        about: 'Allians International College brings together diverse cultures and ideas.',
        courses: ['Computer Applications', 'Commerce', 'Languages', 'Social Sciences', 'Fine Arts'],
        facilities: ['Cultural Center', 'Language Lab', 'Library', 'Sports Complex', 'Art Studio'],
        achievements: ['Cultural Excellence Award', 'Diversity Champion', 'Community Service Award'],
        students: '1,000+',
        faculty: '70+',
        logo: '/images/allians.png',
        component: AlliansCollegePage,
    },
];

const CollegesPage = () => {
    const [mounted, setMounted] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) return null;

    const currentCollege = collegesData[selectedTab];
    const SelectedCollegeComponent = currentCollege.component;

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            <Navbar />

            {/* ==================== TOP HERO ==================== */}
            <Box
                sx={{
                    background: BRAND_GRADIENT,
                    pt: { xs: 6, sm: 8, md: 10 },
                    pb: { xs: 10, sm: 12, md: 16 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: '10%', right: '5%', width: { xs: 200, md: 350 }, height: { xs: 200, md: 350 }, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: '5%', left: '5%', width: { xs: 150, md: 250 }, height: { xs: 150, md: 250 }, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    <Fade in timeout={800}>
                        <Box>
                            <Chip
                                label="OUR INSTITUTIONS"
                                size={isMobile ? 'small' : 'medium'}
                                sx={{
                                    mb: { xs: 2, md: 3 },
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '0.8rem', md: '0.95rem' },
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                }}
                            />

                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: '1.75rem', sm: '2.2rem', md: '3.2rem', lg: '4.2rem' },
                                    fontWeight: 800,
                                    mb: 2,
                                    color: 'white',
                                    lineHeight: 1.25,
                                }}
                            >
                                Welcome to{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        display: { xs: 'block', sm: 'inline' },
                                        background: 'linear-gradient(135deg, #FFF6B0 0%, #FFD93D 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        mt: { xs: 1, sm: 0 },
                                    }}
                                >
                                    {currentCollege.name}
                                </Box>
                            </Typography>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* ==================== OVERLAPPING CARD ==================== */}
            <Container maxWidth="xl" sx={{ mt: { xs: -6, md: -10 }, mb: 5, position: 'relative', zIndex: 2, px: { xs: 2, sm: 3 } }}>
                <Grow in timeout={1000}>
                    <Paper elevation={24} sx={{ p: { xs: 2.5, sm: 3.5, md: 5 }, borderRadius: { xs: 3, md: 4 }, bgcolor: 'white' }}>
                        <Grid container spacing={{ xs: 3, md: 4 }}>
                            <Grid item xs={12} md={8}>
                                {/* Logo + Title */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 1.5, sm: 2 }}
                                    alignItems={{ xs: 'center', sm: 'center' }}
                                    textAlign={{ xs: 'center', sm: 'left' }}
                                    sx={{ mb: { xs: 2, md: 3 } }}
                                >
                                    <CollegeLogo college={currentCollege} size={isMobile ? 60 : 70} />
                                    <Box>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: { xs: '1.35rem', sm: '1.6rem', md: '2.2rem' },
                                                lineHeight: 1.25,
                                            }}
                                        >
                                            {currentCollege.name}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        mb: { xs: 2.5, md: 3 },
                                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.15rem' },
                                        lineHeight: 1.7,
                                        textAlign: { xs: 'center', sm: 'left' },
                                    }}
                                >
                                    {currentCollege.about}
                                </Typography>

                                {/* Contact Info */}
                                <Grid container spacing={{ xs: 1.5, md: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <LocationIcon sx={{ color: currentCollege.color, fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: { xs: '0.85rem', md: '1.05rem' },
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {currentCollege.location}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <PhoneIcon sx={{ color: currentCollege.color, fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '1.05rem' } }}>
                                                {currentCollege.phone}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <EmailIcon sx={{ color: currentCollege.color, fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: { xs: '0.85rem', md: '1.05rem' },
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {currentCollege.email}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <WebIcon sx={{ color: currentCollege.color, fontSize: { xs: 20, md: 24 }, flexShrink: 0 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: { xs: '0.85rem', md: '1.05rem' },
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {currentCollege.website}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Stats Cards */}
                            <Grid item xs={12} md={4}>
                                <Stack spacing={{ xs: 1.5, md: 2 }}>
                                    <Paper sx={{ p: { xs: 1.5, md: 2 }, bgcolor: alpha(currentCollege.color, 0.08), borderRadius: 2 }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <GroupsIcon sx={{ color: currentCollege.color, fontSize: { xs: 26, md: 32 } }} />
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: '1.4rem' }, lineHeight: 1.2 }}>
                                                    {currentCollege.students}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
                                                    Students
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                    <Paper sx={{ p: { xs: 1.5, md: 2 }, bgcolor: alpha(currentCollege.color, 0.08), borderRadius: 2 }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <PersonIcon sx={{ color: currentCollege.color, fontSize: { xs: 26, md: 32 } }} />
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: '1.4rem' }, lineHeight: 1.2 }}>
                                                    {currentCollege.faculty}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
                                                    Faculty Members
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                    <Paper sx={{ p: { xs: 1.5, md: 2 }, bgcolor: alpha(currentCollege.color, 0.08), borderRadius: 2 }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <CalendarIcon sx={{ color: currentCollege.color, fontSize: { xs: 26, md: 32 } }} />
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: '1.4rem' }, lineHeight: 1.2 }}>
                                                    {currentCollege.established}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
                                                    Established
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grow>
            </Container>

            {/* ==================== COLLEGES TABS ==================== */}
            <Container maxWidth="xl" sx={{ pb: { xs: 5, md: 8 }, px: { xs: 2, sm: 3 } }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 800,
                        mb: 1,
                        textAlign: 'center',
                        fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.4rem' },
                    }}
                >
                    Explore All Our Colleges
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        mb: { xs: 3, md: 4 },
                        textAlign: 'center',
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
                        px: { xs: 1, md: 0 },
                    }}
                >
                    Click on any tab below to view that college's information
                </Typography>

                <Paper elevation={2} sx={{ borderRadius: 3, mb: 3, overflow: 'hidden', bgcolor: 'white' }}>
                    <Tabs
                        value={selectedTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1.1rem' },
                                minHeight: { xs: 60, md: 78 },
                                minWidth: { xs: 'auto', md: 180 },
                                px: { xs: 1.5, md: 2 },
                                py: { xs: 1.2, md: 2 },
                            },
                            '& .Mui-selected': {
                                color: currentCollege.color,
                                fontWeight: 800,
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: currentCollege.color,
                                height: 4,
                                borderRadius: '4px 4px 0 0',
                            },
                            '& .MuiTab-iconWrapper': {
                                mr: { xs: 0.5, md: 1 },
                            },
                        }}
                    >
                        {collegesData.map((college) => (
                            <Tab
                                key={college.id}
                                icon={
                                    <CollegeLogo
                                        college={college}
                                        size={isMobile ? 30 : 40}
                                        selected={selectedTab === college.id}
                                        forTab
                                    />
                                }
                                iconPosition="start"
                                label={college.name}
                            />
                        ))}
                    </Tabs>
                </Paper>

                {/* ==================== TAB CONTENT ==================== */}
                <Fade in key={selectedTab} timeout={500}>
                    <Box>
                        <SelectedCollegeComponent />
                    </Box>
                </Fade>
            </Container>

            <HomeFooter />
        </Box>
    );
};

export default CollegesPage;